import * as _ from 'lodash';
import { useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import { FILE_PATH_IN_REPORTS, SNIPPET_DETECTION } from '../../../../shared/featureFlags/flagNames';
import { DependencyInfoOptions, dependencyInfoOptionsWithDescriptions } from '../../../../shared/reports';
import { useParams } from '../../../contexts/RouterContext';
import { window } from '../../../globals';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { useOrganizationPackageLabels } from '../../../hooks/api/packageLabels';
import useIsLicenseConcludedEnabled from '../../../hooks/features/useIsLicenseConcludedEnabled';
import { selectOptionFactory } from '../../../utils';
import { FilterPanel } from '../../generic/Filter';
import FilterCardCheckboxes from '../../generic/Filter/FilterCardCheckboxes';
import FilterCardMultiSelect from '../../generic/Filter/FilterCardMultiSelect';
import FilterCardNestedCheckboxes from '../../generic/Filter/FilterCardNestedCheckboxes';
import { licenseIncludeOptions } from '../../projects/generic/ReportGenerationV2/shared/constants';
import { ReportOptionDetails } from '../../projects/generic/ReportGenerationV2/utils';
import { useCustomReportOptions } from '../hooks/useCustomReportOptions';
import { useReportFormatSelector } from '../hooks/useFormatSelector';
import { useReportType } from '../hooks/useReportType';
import { isReleaseGroupScope } from '../types';
import type { AttributionReportIncludeOptions, DependencyInfoOption } from '../../../../shared/reports';
import type { SelectOptionWithAttributes } from '../../../types';
import type { ReportCategory } from '../../projects/generic/shared/types';
import type { ReportsV3Params, ReportType } from '../types';

/**
 * Maps V3 ReportType values to V2 category strings used in ReportOptionDetails.
 */
const reportTypeToCategoryMap: Record<ReportType, ReportCategory> = {
  licensing: 'licensing',
  sbom: 'sbom',
  vulnerability: 'vulnerability',
  remediationGuidance: 'remediation-guidance',
};

const labelOverrides: Partial<Record<string, string>> = {
  includeProjectLicense: 'Declared Licenses',
};

const toOption = (key: string & keyof typeof ReportOptionDetails): SelectOptionWithAttributes => {
  const { title, description } = ReportOptionDetails[key] ?? {};
  return {
    label: labelOverrides[key] ?? title ?? key,
    value: key,
    tooltip: description,
  };
};

const cycloneDXFormats = new Set(['CYCLONEDX_JSON', 'CYCLONEDX_XML']);

/**
 * Custom report options users will select to conditionally include or exclude data from reports.
 */
export const CustomOptions: VoidFunctionComponent = () => {
  const reportType = useReportType();
  const { value: formatOption } = useReportFormatSelector();
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);

  const isLicenseConcludedEnabled = useIsLicenseConcludedEnabled(window?.user?.organizationId ?? 0);
  const isSnippetDetectionEnabled = useIsFeatureEnabled(SNIPPET_DETECTION);
  const isFilePathInReportsEnabled = useIsFeatureEnabled(FILE_PATH_IN_REPORTS);

  const category = reportTypeToCategoryMap[reportType];
  const format = formatOption.value;

  const { flatOptions, licenseOptions } = useMemo(() => {
    const filtered = Object.values(ReportOptionDetails)
      .filter((detail) => {
        if (!detail) {
          return false;
        }
        const { key, format: applicableFormats, category: applicableCategories } = detail;

        if (key === 'includeConcludedLicenses' && !isLicenseConcludedEnabled) {
          return false;
        }
        if (key === 'includeFOSSADependencies' && !isReleaseGroup) {
          return false;
        }
        if (key === 'includeSnippetDependencies' && !isSnippetDetectionEnabled) {
          return false;
        }

        const isCorrectCategory = applicableCategories.includes(category);
        const isApplicableFormat = applicableFormats.includes(format);
        return isCorrectCategory && isApplicableFormat;
      })
      .map(({ key }) => toOption(key as string & keyof typeof ReportOptionDetails));

    const [license, flat] = _.partition(
      filtered,
      (option) => licenseIncludeOptions.has(option.value as keyof AttributionReportIncludeOptions),
    );

    return { flatOptions: flat, licenseOptions: license };
  }, [category, format, isLicenseConcludedEnabled, isReleaseGroup, isSnippetDetectionEnabled]);

  const dependencyMetadataOptions = useMemo(() => {
    return Object.entries(DependencyInfoOptions)
      .filter(([key]) => {
        if (key === 'FilePath') {
          return isFilePathInReportsEnabled;
        }
        if (key === 'Project') {
          return isReleaseGroup && reportType === 'licensing';
        }
        if (key === 'ConcludedLicense' && !isLicenseConcludedEnabled) {
          return false;
        }
        if (key === 'IssueNotes' || key === 'Source') {
          return reportType === 'licensing';
        }
        if (key === 'ProjectUrl' && cycloneDXFormats.has(format)) {
          return false;
        }
        return true;
      })
      .map(([key, value]) => ({
        label: value,
        value: key,
        tooltip: dependencyInfoOptionsWithDescriptions[key as DependencyInfoOption],
      }));
  }, [format, isFilePathInReportsEnabled, isLicenseConcludedEnabled, isReleaseGroup, reportType]);

  /**
   * Fetch and handle package labels.
   */
  const { data: labelsResponse, isLoading: areLabelsLoading } = useOrganizationPackageLabels();
  const availableLabels = useMemo(
    () => (labelsResponse?.packageLabels ?? []).map((label) => selectOptionFactory(label.name)),
    [labelsResponse],
  );

  const flatOptionKeys = useMemo(() => flatOptions.map((o) => o.value), [flatOptions]);
  const licenseOptionKeys = useMemo(() => licenseOptions.map((o) => o.value), [licenseOptions]);

  // This hook manages the state for which custom report options are selected.
  // It uses the URL query params to store the selected options.
  const {
    includeValues,
    setIncludeOptions,
    licenseValues,
    setLicenseOptions,
    dependencyMetadataValues,
    setDependencyMetadata,
    excludePackageLabelValues,
    setExcludePackageLabels,
  } = useCustomReportOptions(flatOptionKeys, licenseOptionKeys);

  const shouldShowExcludePackageLabels = reportType === 'licensing' || reportType === 'sbom';

  return (
    <FilterPanel title="Options">
      <div className="width-full">
        <FilterCardCheckboxes
          label="Included"
          options={flatOptions}
          values={includeValues}
          onChange={setIncludeOptions}
          tooltipPlacement="left"
        />
        {licenseOptions.length > 0 && (
          <FilterCardNestedCheckboxes
            options={licenseOptions}
            values={licenseValues}
            onChange={setLicenseOptions}
            parentCheckboxLabel="List of licenses"
            parentTooltip="A complete collection of all licenses detected across your project's dependencies."
            tooltipPlacement="left"
          />
        )}
      </div>
      {shouldShowExcludePackageLabels && (
        <FilterCardMultiSelect
          label="Exclude Package Labels"
          placeholder={areLabelsLoading ? 'Loading...' : 'Select one or more...'}
          options={availableLabels}
          values={excludePackageLabelValues}
          onChange={setExcludePackageLabels}
          isDisabled={availableLabels.length === 0}
        />
      )}
      <FilterCardCheckboxes
        label="Dependency Metadata"
        options={dependencyMetadataOptions}
        values={dependencyMetadataValues}
        onChange={setDependencyMetadata}
        tooltipPlacement="left"
      />
    </FilterPanel>
  );
};
